import {
  ACTIVO_SIN_COTIZACION_LABEL,
  filterActivoSelectableAssets,
  SIN_COTIZAR,
} from './quotationAssets';

/**
 * Precio unitario de cotización para crédito máximo (origination usa number; API puede mandar string).
 */
export function parseCreditoMaximoUnitSale(saleValue: unknown): number | null {
  if (saleValue === SIN_COTIZAR || saleValue == null) return null;
  if (typeof saleValue === 'number') {
    if (Number.isNaN(saleValue)) return null;
    return saleValue;
  }
  if (typeof saleValue === 'string') {
    const t = saleValue.trim();
    if (t === '' || t === SIN_COTIZAR) return null;
    const n = parseFloat(t.replace(',', '.'));
    if (Number.isNaN(n)) return null;
    return n;
  }
  return null;
}

/** Hay precio unitario válido y > 0 para multiplicar por cantidad (auto-cálculo). */
export function hasCreditoMaximoUnitSale(saleValue: unknown): boolean {
  const n = parseCreditoMaximoUnitSale(saleValue);
  return n != null && n > 0;
}

/**
 * Monto = cantidad × precio unitario; evita ruido de coma flotante (2 decimales).
 */
export function computeMontoOtorgadoString(unit: number, quantity: number): string {
  const n = unit * quantity;
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

/**
 * Precio unitario: primero [saleValue] del producto (GET ms-application), luego lista de cotizaciones.
 */
/** Mapea saleValue del JSON de crédito máximo (camelCase / snake_case). */
export function mapSaleValueFromCreditoMaximoApi(p: Record<string, unknown>): number | undefined {
  const raw = p.saleValue ?? p.sale_value;
  if (raw == null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/** Equivalente a ítem "Moneda" en solicitud: sin código de activo persistido. */
export function isProductoSinActivoMontoOtorgado(product: { asset_code?: string }): boolean {
  const c = product.asset_code;
  return c == null || String(c).trim() === '';
}

/** Texto columna Activo en monto a otorgar / comité (sin guion para "solo moneda"). */
export function formatActivoMontoOtorgadoLabel(product: {
  asset_name?: string;
  asset_code?: string;
}): string {
  if (isProductoSinActivoMontoOtorgado(product)) return ACTIVO_SIN_COTIZACION_LABEL;
  if (product.asset_name) return product.asset_name;
  return product.asset_code ?? ACTIVO_SIN_COTIZACION_LABEL;
}

/** Combo Activo: primero "Moneda", luego cotizaciones (excl. USD/INF como originación paso 3). */
export function buildActivoMontoOtorgadoDropdownOptions(
  availableAssets: Array<{ code: string; name: string; saleValue?: number | string }>,
  allowedAssetCodes?: string[] | null
): Array<{ code: string; name: string; saleValue?: number | string }> {
  let filtered = filterActivoSelectableAssets(availableAssets);
  if (allowedAssetCodes != null) {
    filtered = filtered.filter((a) => allowedAssetCodes.includes(a.code));
  }
  return [{ code: '', name: ACTIVO_SIN_COTIZACION_LABEL }, ...filtered];
}

export function resolveUnitSaleForProduct(
  product: { asset_code?: string; saleValue?: number },
  assets: Array<{ code: string; saleValue?: number | string }>
): number | null {
  if (product.saleValue != null && Number.isFinite(product.saleValue) && product.saleValue > 0) {
    return product.saleValue;
  }
  if (isProductoSinActivoMontoOtorgado(product)) return null;
  const asset = assets.find((a) => a.code === product.asset_code);
  if (!asset) return null;
  const p = parseCreditoMaximoUnitSale(asset.saleValue);
  return p != null && p > 0 ? p : null;
}
